import { Contact, RecipientPostageDecision, UserSearchResult } from '../types';
import { cacheKeyFromAddress, normalizeQixitAddress } from '../utils/qixitAddress';

export type OutboundPostageSource = 'fetch' | 'send' | 'default' | 'insufficient';

export interface OutboundPostageEntry {
  rate: number;
  updatedAt: number;
  source: OutboundPostageSource;
}

export interface ContactLocalStoreRecord {
  version: 1;
  cachedAt: number;
  contacts: Contact[];
  outboundPostageByAlias: Record<string, OutboundPostageEntry>;
  lookupByAlias: Record<string, UserSearchResult>;
  /** Set when an explicit refresh confirms the identity no longer resolves. */
  identityNotFoundByAlias?: Record<string, true>;
}

const STORE_VERSION = 1 as const;

const getStoreKey = (username: string): string =>
  `contact_book:v${STORE_VERSION}:${username.trim().toLowerCase()}`;

export function readContactLocalStore(username: string): ContactLocalStoreRecord | null {
  if (!username?.trim()) return null;
  try {
    const raw = localStorage.getItem(getStoreKey(username));
    if (!raw) {
      return migrateLegacyComposeCache(username);
    }
    const parsed = JSON.parse(raw) as ContactLocalStoreRecord;
    if (parsed?.version !== STORE_VERSION || !Array.isArray(parsed.contacts)) {
      return migrateLegacyComposeCache(username);
    }
    return {
      version: STORE_VERSION,
      cachedAt: typeof parsed.cachedAt === 'number' ? parsed.cachedAt : 0,
      contacts: parsed.contacts,
      outboundPostageByAlias: parsed.outboundPostageByAlias ?? {},
      lookupByAlias: parsed.lookupByAlias ?? {},
      identityNotFoundByAlias: parsed.identityNotFoundByAlias ?? {},
    };
  } catch {
    return migrateLegacyComposeCache(username);
  }
}

function migrateLegacyComposeCache(username: string): ContactLocalStoreRecord | null {
  try {
    const legacyKey = `compose_contacts_cache:v2:${username.trim().toLowerCase()}`;
    const raw = localStorage.getItem(legacyKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { cachedAt?: number; contacts?: Contact[] };
    if (!Array.isArray(parsed?.contacts)) return null;
    const record: ContactLocalStoreRecord = {
      version: STORE_VERSION,
      cachedAt: typeof parsed.cachedAt === 'number' ? parsed.cachedAt : Date.now(),
      contacts: parsed.contacts,
      outboundPostageByAlias: {},
      lookupByAlias: {},
    };
    writeContactLocalStore(username, record);
    localStorage.removeItem(legacyKey);
    return record;
  } catch {
    return null;
  }
}

export function writeContactLocalStore(username: string, record: ContactLocalStoreRecord): void {
  if (!username?.trim()) return;
  try {
    localStorage.setItem(getStoreKey(username), JSON.stringify(record));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function hasContactLocalStore(username: string): boolean {
  const record = readContactLocalStore(username);
  return !!record?.contacts?.length;
}

function emptyRecord(): ContactLocalStoreRecord {
  return {
    version: STORE_VERSION,
    cachedAt: Date.now(),
    contacts: [],
    outboundPostageByAlias: {},
    lookupByAlias: {},
    identityNotFoundByAlias: {},
  };
}

export function mergeContactsIntoLocalStore(username: string, contacts: Contact[]): ContactLocalStoreRecord {
  const existing = readContactLocalStore(username) ?? emptyRecord();
  const record: ContactLocalStoreRecord = {
    ...existing,
    cachedAt: Date.now(),
    contacts,
  };
  writeContactLocalStore(username, record);
  return record;
}

export function upsertContactInLocalStore(username: string, contact: Contact): ContactLocalStoreRecord {
  const existing = readContactLocalStore(username) ?? emptyRecord();
  const key = (contact.address || '').trim().toLowerCase();
  const contacts = existing.contacts.filter(
    (c) => (c.address || '').trim().toLowerCase() !== key
  );
  contacts.push(contact);
  const record: ContactLocalStoreRecord = {
    ...existing,
    cachedAt: Date.now(),
    contacts,
  };
  writeContactLocalStore(username, record);
  return record;
}

export function removeContactFromLocalStore(username: string, contactId: number): ContactLocalStoreRecord {
  const existing = readContactLocalStore(username) ?? emptyRecord();
  const removed = existing.contacts.find((c) => c.contactID === contactId);
  const record: ContactLocalStoreRecord = {
    ...existing,
    cachedAt: Date.now(),
    contacts: existing.contacts.filter((c) => c.contactID !== contactId),
  };
  if (removed?.address) {
    const aliasKey = cacheKeyFromAddress(removed.address);
    if (aliasKey) {
      delete record.outboundPostageByAlias[aliasKey];
      delete record.lookupByAlias[aliasKey];
      if (record.identityNotFoundByAlias) {
        delete record.identityNotFoundByAlias[aliasKey];
      }
    }
  }
  writeContactLocalStore(username, record);
  return record;
}

export function setLocalOutboundPostage(
  username: string,
  addressOrAlias: string,
  rate: number,
  source: OutboundPostageSource
): void {
  const aliasKey = cacheKeyFromAddress(
    normalizeQixitAddress(addressOrAlias) ?? addressOrAlias
  );
  if (!aliasKey || !username?.trim()) return;

  const existing = readContactLocalStore(username) ?? emptyRecord();
  existing.outboundPostageByAlias[aliasKey] = {
    rate: Number.isFinite(rate) && rate >= 0 ? rate : 0,
    updatedAt: Date.now(),
    source,
  };
  existing.cachedAt = Date.now();
  writeContactLocalStore(username, existing);
}

export function getLocalOutboundPostage(username: string, addressOrAlias: string): number | undefined {
  const aliasKey = cacheKeyFromAddress(
    normalizeQixitAddress(addressOrAlias) ?? addressOrAlias
  );
  if (!aliasKey || !username?.trim()) return undefined;
  const record = readContactLocalStore(username);
  return record?.outboundPostageByAlias[aliasKey]?.rate;
}

export function resolveOutboundPostageEstimate(
  username: string,
  addressOrAlias: string,
  defaultMaxPay: number
): number {
  const cached = getLocalOutboundPostage(username, addressOrAlias);
  if (cached !== undefined) return cached;
  return Number.isFinite(defaultMaxPay) && defaultMaxPay >= 0 ? defaultMaxPay : 0;
}

export function setLocalContactLookup(
  username: string,
  addressOrAlias: string,
  info: UserSearchResult
): void {
  const aliasKey = cacheKeyFromAddress(
    normalizeQixitAddress(addressOrAlias) ?? addressOrAlias
  );
  if (!aliasKey || !username?.trim()) return;

  const existing = readContactLocalStore(username) ?? emptyRecord();
  existing.lookupByAlias[aliasKey] = info;
  if (existing.identityNotFoundByAlias) {
    delete existing.identityNotFoundByAlias[aliasKey];
  }
  existing.cachedAt = Date.now();
  writeContactLocalStore(username, existing);
}

export function setLocalIdentityNotFound(
  username: string,
  addressOrAlias: string,
  notFound: boolean
): void {
  const aliasKey = cacheKeyFromAddress(
    normalizeQixitAddress(addressOrAlias) ?? addressOrAlias
  );
  if (!aliasKey || !username?.trim()) return;

  const existing = readContactLocalStore(username) ?? emptyRecord();
  if (!existing.identityNotFoundByAlias) {
    existing.identityNotFoundByAlias = {};
  }

  if (notFound) {
    existing.identityNotFoundByAlias[aliasKey] = true;
    delete existing.lookupByAlias[aliasKey];
  } else {
    delete existing.identityNotFoundByAlias[aliasKey];
  }

  existing.cachedAt = Date.now();
  writeContactLocalStore(username, existing);
}

export function getLocalIdentityNotFoundMap(username: string): Record<string, boolean> {
  const record = readContactLocalStore(username);
  if (!record?.identityNotFoundByAlias) return {};

  const map: Record<string, boolean> = {};
  for (const [aliasKey, flagged] of Object.entries(record.identityNotFoundByAlias)) {
    if (!flagged) continue;
    const usernamePart = aliasKey.split('@')[0]?.toLowerCase();
    if (usernamePart) {
      map[usernamePart] = true;
    }
  }
  return map;
}

export function getLocalContactLookup(
  username: string,
  addressOrAlias: string
): UserSearchResult | undefined {
  const aliasKey = cacheKeyFromAddress(
    normalizeQixitAddress(addressOrAlias) ?? addressOrAlias
  );
  if (!aliasKey || !username?.trim()) return undefined;
  return readContactLocalStore(username)?.lookupByAlias[aliasKey];
}

export function getLocalLookupMap(username: string): Record<string, UserSearchResult> {
  const record = readContactLocalStore(username);
  if (!record) return {};

  const map: Record<string, UserSearchResult> = {};
  for (const [aliasKey, info] of Object.entries(record.lookupByAlias)) {
    const usernamePart = aliasKey.split('@')[0]?.toLowerCase();
    if (usernamePart) {
      map[usernamePart] = info;
    }
  }
  return map;
}

export function getLocalOutboundPostageMap(username: string): Record<string, number> {
  const record = readContactLocalStore(username);
  if (!record) return {};

  const map: Record<string, number> = {};
  for (const [aliasKey, entry] of Object.entries(record.outboundPostageByAlias)) {
    const usernamePart = aliasKey.split('@')[0]?.toLowerCase();
    if (usernamePart) {
      map[usernamePart] = entry.rate;
    }
  }
  return map;
}

export function applySendPostageResults(
  username: string,
  decisions: RecipientPostageDecision[] | undefined
): void {
  if (!username?.trim() || !decisions?.length) return;
  for (const decision of decisions) {
    if (!decision.address) continue;
    setLocalOutboundPostage(username, decision.address, decision.requiredPostage, 'send');
  }
}

export function setLocalInboundRate(username: string, addressOrAlias: string, deliveryRequired: number): void {
  const normalized = (normalizeQixitAddress(addressOrAlias) ?? addressOrAlias).trim().toLowerCase();
  if (!normalized || !username?.trim()) return;

  const existing = readContactLocalStore(username) ?? emptyRecord();
  existing.contacts = existing.contacts.map((contact) => {
    if ((contact.address || '').trim().toLowerCase() !== normalized) return contact;
    return { ...contact, deliveryRequired };
  });
  existing.cachedAt = Date.now();
  writeContactLocalStore(username, existing);
}

export function setLocalNickname(
  username: string,
  addressOrAlias: string,
  nickname: string,
  aliasDisplayName?: string
): void {
  const normalized = (normalizeQixitAddress(addressOrAlias) ?? addressOrAlias).trim().toLowerCase();
  if (!normalized || !username?.trim()) return;

  const existing = readContactLocalStore(username) ?? emptyRecord();
  existing.contacts = existing.contacts.map((contact) => {
    if ((contact.address || '').trim().toLowerCase() !== normalized) return contact;
    return {
      ...contact,
      nickname: nickname.trim() || undefined,
      ...(aliasDisplayName ? { displayName: aliasDisplayName } : {}),
    };
  });
  existing.cachedAt = Date.now();
  writeContactLocalStore(username, existing);
}
